import type { ComponentType, ReactNode } from 'react';
import { ErrorBoundary, type FallbackProps } from 'react-error-boundary';
import { useLocation } from 'react-router-dom';

/**
 * The error boundary of an authenticated route.
 *
 * Every authenticated route renders it at the same place in the tree, so React keeps ONE
 * boundary instance across navigations. Without a reset, a page that failed once (in render,
 * or in an effect cleanup while it was being left) kept the error page on every later route:
 * the sidebar, the header links and browser back changed the URL but nothing else. A new
 * location resets it.
 */
export function RouteErrorBoundary({
  fallback,
  children,
}: {
  fallback: ComponentType<FallbackProps>;
  children: ReactNode;
}) {
  const { pathname } = useLocation();
  return (
    <ErrorBoundary FallbackComponent={fallback} resetKeys={[pathname]}>
      {children}
    </ErrorBoundary>
  );
}

export default RouteErrorBoundary;
